// Error-monitoring service entry point.

import type { CaptureContext, MonitoringProvider } from "./types";
import { stubMonitoringProvider } from "./providers/stub";
import { sentryMonitoringProvider } from "./providers/sentry";

export type {
  CaptureContext,
  ErrorSource,
  MonitoringProvider,
  MonitoringProviderName,
} from "./types";

export function resolveMonitoringProvider(): MonitoringProvider {
  const kind = readProvider();
  switch (kind) {
    case "sentry":
      return sentryMonitoringProvider;
    case "stub":
    default:
      return stubMonitoringProvider;
  }
}

/**
 * Fire-and-forget error capture. Never throws — a failing monitor must not
 * take down the caller (or emit customer-visible errors). Provider failures
 * are logged internally and swallowed.
 */
export function captureException(error: unknown, context?: CaptureContext): void {
  const provider = resolveMonitoringProvider();
  void Promise.resolve()
    .then(() => provider.capture(error, context))
    .then(() => undefined)
    .catch((err) => {
      const msg = err instanceof Error ? err.message : "unknown_error";
      console.error(`[monitor] provider ${provider.name} threw: ${msg}`);
    });
}

function readProvider(): string {
  if (typeof process !== "undefined" && process.env?.ERROR_PROVIDER)
    return process.env.ERROR_PROVIDER.toLowerCase();
  if (typeof import.meta !== "undefined") {
    const env = (import.meta as { env?: Record<string, string | undefined> }).env;
    if (env?.VITE_ERROR_PROVIDER) return env.VITE_ERROR_PROVIDER.toLowerCase();
    if (env?.VITE_SENTRY_DSN) return "sentry";
  }
  if (typeof process !== "undefined" && process.env?.SENTRY_DSN) return "sentry";
  return "stub";
}
